// src/pages/public-access/CodeVerificationPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { 
  Lock, 
  Eye, 
  EyeOff, 
  Shield, 
  FileText, 
  AlertCircle,
  Loader2,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import toast from 'react-hot-toast';

// Import your API service - adjust path as needed
import { contractAccessService } from '../../services/';

const CodeVerificationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch contract information using the token
  const { 
    data: contractInfo, 
    isLoading: contractLoading, 
    error: contractError 
  } = useQuery(
    ['contractByToken', token],
    () => contractAccessService.getContractByToken(token),
    {
      enabled: !!token,
      retry: 1,
      onError: (error) => {
        console.error('Error fetching contract:', error);
        if (error.response?.status === 400) {
          toast.error('Link invalid sau expirat');
        } else {
          toast.error('Eroare la încărcarea contractului');
        }
      }
    }
  );

  // Verify access code mutation
  const verifyCodeMutation = useMutation(
    (verificationData) => contractAccessService.verifyAccessCode(token, verificationData),
    {
      onSuccess: (data) => {
        toast.success('Cod verificat cu succes!');
        // Navigate to document viewer
        navigate(`/contract-access/${token}/dashboard`);
      },
      onError: (error) => {
        console.error('Error verifying code:', error);
        if (error.response?.status === 400) {
          toast.error('Cod de acces incorect');
        } else {
          toast.error('Eroare la verificarea codului');
        }
        setIsSubmitting(false);
      }
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code || code.length !== 6) {
      toast.error('Vă rugăm să introduceți un cod valid de 6 caractere');
      return;
    }

    setIsSubmitting(true);
    verifyCodeMutation.mutate({ Code: code.toUpperCase() });
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 6) {
      setCode(value);
    }
  };

  // Auto-focus on code input when component mounts
  useEffect(() => {
    const codeInput = document.getElementById('accessCode');
    if (codeInput) {
      codeInput.focus();
    }
  }, [contractInfo]);

  // If already accessed and doesn't require code, redirect to viewer
  useEffect(() => {
    if (contractInfo && !contractInfo.requiresCode) {
      navigate(`/contract-access/${token}/view`);
    }
  }, [contractInfo, navigate, token]);

  if (contractLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Se încarcă...
            </h2>
            <p className="text-gray-600">
              Verificăm informațiile contractului
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (contractError || !contractInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Acces Interzis
            </h2>
            <p className="text-gray-600 mb-6">
              Link-ul este invalid sau a expirat. Vă rugăm să contactați expeditorul pentru un nou link.
            </p>
            <button
              onClick={() => window.history.back()}
              className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
            >
              Înapoi
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-primary-500 text-white p-6 text-center">
          <Shield className="w-12 h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Acces Securizat</h1>
          <p className="text-primary-100">
            Verificare cod de acces pentru contract
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Contract Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center mb-3">
              <FileText className="w-5 h-5 text-gray-600 mr-2" />
              <span className="font-medium text-gray-900">
                Contract: {contractInfo.contractNumber}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              <p><strong>Client:</strong> {contractInfo.clientName}</p>
              <p><strong>Status:</strong> {contractInfo.status}</p>
            </div>
          </div>

          {/* Instructions */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Introduceți codul de acces
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Codul de 6 caractere a fost trimis pe email. Introduceți-l pentru a accesa contractul.
            </p>
          </div>

          {/* Code Input Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="accessCode" className="block text-sm font-medium text-gray-700 mb-2">
                Cod de acces
              </label>
              <div className="relative">
                <input
                  type={showCode ? 'text' : 'password'}
                  id="accessCode"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="XXXXXX"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-center text-lg tracking-widest uppercase"
                  maxLength={6}
                  autoComplete="off"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowCode(!showCode)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isSubmitting}
                >
                  {showCode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Codul conține doar litere mari și cifre
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || code.length !== 6}
              className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Se verifică...
                </>
              ) : (
                <>
                  Verifică codul
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Nu ați primit codul?</p>
                <p>
                  Verificați folderul de spam sau contactați expeditorul pentru a retrimite codul.
                </p>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              <Lock className="w-3 h-3 inline mr-1" />
              Conexiune securizată și criptată
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeVerificationPage;